// WEBAPP-021: Caricamento paginato con infinite scroll - Lazy loading
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Plus, AlertCircle, Dumbbell, ListFilter, CheckCircle, RefreshCw, Replace, ImageOff, Tags } from 'lucide-react';
import ExerciseForm from '../exercises/ExerciseForm';
import ExerciseList from '../ExerciseList';
import ExerciseApprovalPanel from './ExerciseApprovalPanel';
import ExerciseReplacementPanel from './ExerciseReplacementPanel';
import ExerciseNoStaticImagePanel from './ExerciseNoStaticImagePanel';
import ExerciseGymAliasesPanel from './ExerciseGymAliasesPanel';

const ExerciseAdminPanel = () => {
    const { getAuthenticatedApi, isAdmin } = useAuth();
    const api = getAuthenticatedApi();
    const userIsAdmin = isAdmin(); // Verifica se l'utente è un admin

    const [selectedTab, setSelectedTab] = useState('list');
    const [editingExercise, setEditingExercise] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [pendingCount, setPendingCount] = useState(0); // Contatore per esercizi in attesa
    const [refreshKey, setRefreshKey] = useState(0); // Key per forzare refresh di ExerciseList
    
    // Carica il conteggio degli esercizi in attesa (solo per admin)
    const fetchPendingCount = async () => {
        if (!userIsAdmin) return; // Solo gli admin possono vedere questo conteggio
        
        try {
            const response = await api.get('/pending_exercises.php');
            if (response.data.success) {
                const pendingExercises = response.data.exercises.filter(ex => ex.status === 'pending_review');
                setPendingCount(pendingExercises.length);
            }
        } catch (err) {
            console.error('Errore nel recupero degli esercizi in attesa:', err);
        }
    };

    // Carica il conteggio degli esercizi in attesa all'inizializzazione
    useEffect(() => {
        if (userIsAdmin) { // Solo gli admin caricano i dati delle approvazioni
            fetchPendingCount();
        }
    }, [userIsAdmin]);

    // Gestisce l'eliminazione di un esercizio
    const handleExerciseDelete = async (exerciseId) => {
        if (!window.confirm('Sei sicuro di voler eliminare questo esercizio?')) {
            return;
        }

        try {
            await api.delete(`/esercizi.php?id=${exerciseId}`);
            // Forza refresh di ExerciseList
            setRefreshKey(prev => prev + 1);
        } catch (err) {
            console.error('Errore nella eliminazione:', err);
        }
    };

    // Gestisce la modifica di un esercizio
    const handleExerciseEdit = (exercise) => {
        setEditingExercise(exercise);
        setShowModal(true);
    };

    // Gestisce la chiusura del modal di modifica
    const handleCloseModal = () => {
        setEditingExercise(null);
        setShowModal(false);
    };

    // Gestisce l'aggiunta o la modifica di un esercizio
    const handleExerciseSaved = async (newExercise) => {
        // Forza refresh di ExerciseList
        setRefreshKey(prev => prev + 1);
        
        // Reset dello stato di modifica
        setEditingExercise(null);
        setShowModal(false);
        
        // Cambia la tab per mostrare la lista
        setSelectedTab('list');
    };

    // Callback quando un esercizio viene approvato o rifiutato
    const handleApprovalAction = () => {
        // Forza refresh di ExerciseList
        setRefreshKey(prev => prev + 1);
        fetchPendingCount(); // Aggiorna il contatore
    };

    const handleRefresh = () => {
        // Forza refresh di ExerciseList
        setRefreshKey(prev => prev + 1);
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">
            <div className="flex-1 flex flex-col min-h-0">
                {/* Header fisso */}
                <div className="flex-shrink-0 p-6 pb-4">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Dumbbell className="h-6 w-6" />
                    Gestione Esercizi
                </h2>
                        </div>

                <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={handleRefresh}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                <RefreshCw className="h-4 w-4" />
                                <span>Aggiorna</span>
                            </button>
                    <button
                        onClick={() => setSelectedTab('new')}
                        className="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors flex items-center gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        <span>Nuovo Esercizio</span>
                    </button>
                </div>
            </div>

                </div>

                {/* Tabs container - deve essere flex-1 per prendere lo spazio rimanente */}
            <Tabs
                value={selectedTab}
                onValueChange={setSelectedTab}
                    className="w-full flex-1 flex flex-col min-h-0"
            >
                        <TabsList className={`flex-shrink-0 grid ${userIsAdmin ? 'grid-cols-6' : 'grid-cols-2'} mb-4 w-full max-w-5xl`}>
                    <TabsTrigger value="list" className="flex items-center gap-2">
                        <ListFilter className="h-4 w-4" />
                        <span>Lista Esercizi</span>
                    </TabsTrigger>
                    <TabsTrigger value="new" className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        <span>Nuovo Esercizio</span>
                    </TabsTrigger>
                    {/* Mostra il tab Approvazioni solo agli admin */}
                    {userIsAdmin && (
                        <TabsTrigger value="approval" className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            <span>Approvazioni</span>
                            {pendingCount > 0 && (
                                <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                                    {pendingCount}
                                </span>
                            )}
                        </TabsTrigger>
                    )}
                    {/* Mostra il tab Sostituzione solo agli admin */}
                    {userIsAdmin && (
                        <TabsTrigger value="replacement" className="flex items-center gap-2">
                            <Replace className="h-4 w-4" />
                            <span>Sostituzione</span>
                        </TabsTrigger>
                    )}
                    {userIsAdmin && (
                        <TabsTrigger value="gymAliases" className="flex items-center gap-2">
                            <Tags className="h-4 w-4" />
                            <span>Nomi palestre</span>
                        </TabsTrigger>
                    )}
                    {/* Tab Esercizi senza immagine statica (admin) */}
                    {userIsAdmin && (
                        <TabsTrigger value="noStaticImage" className="flex items-center gap-2">
                            <ImageOff className="h-4 w-4" />
                            <span>Senza immagine</span>
                        </TabsTrigger>
                    )}
                </TabsList>

                        <TabsContent value="list" className="focus:outline-none flex-1 min-h-0 overflow-hidden px-6 pb-6">
                            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-200 dark:border-gray-700 h-full flex flex-col">
                                <div className="flex-1 min-h-0 overflow-y-auto p-6" style={{ overscrollBehavior: 'contain' }}>
                                    <ExerciseList
                                        key={refreshKey} // Key per forzare refresh
                                        esercizi={[]}
                                        onDelete={handleExerciseDelete}
                                        onEdit={handleExerciseEdit}
                                        useInternalLoading={true}
                                    />
                                </div>
                            </div>
                </TabsContent>

                        <TabsContent value="new" className="focus:outline-none flex-1 min-h-0 overflow-hidden">
                            <div className="h-full overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
                    <ExerciseForm onExerciseAdded={handleExerciseSaved} />
                            </div>
                </TabsContent>
                
                {/* Mostra il contenuto del tab Approvazioni solo agli admin */}
                {userIsAdmin && (
                            <TabsContent value="approval" className="focus:outline-none flex-1 min-h-0 overflow-hidden">
                                <div className="h-full flex flex-col">
                                    <div className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-200 dark:border-gray-700 h-full flex flex-col">
                                        <div className="flex-1 min-h-0 overflow-y-auto p-6" style={{ overscrollBehavior: 'contain' }}>
                        <ExerciseApprovalPanel onApprovalAction={handleApprovalAction} />
                                        </div>
                                    </div>
                                </div>
                    </TabsContent>
                )}
                
                {/* Mostra il contenuto del tab Sostituzione solo agli admin */}
                {userIsAdmin && (
                            <TabsContent value="replacement" className="focus:outline-none flex-1 min-h-0 overflow-hidden">
                                <div className="h-full flex flex-col">
                                    <div className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-200 dark:border-gray-700 h-full flex flex-col">
                                        <div className="flex-1 min-h-0 overflow-y-auto p-6" style={{ overscrollBehavior: 'contain' }}>
                        <ExerciseReplacementPanel />
                                        </div>
                                    </div>
                                </div>
                    </TabsContent>
                )}
                {userIsAdmin && (
                            <TabsContent value="gymAliases" className="focus:outline-none flex-1 min-h-0 overflow-hidden">
                                <div className="h-full flex flex-col">
                                    <div className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-200 dark:border-gray-700 h-full flex flex-col">
                                        <div className="flex-1 min-h-0 overflow-y-auto p-6" style={{ overscrollBehavior: 'contain' }}>
                        <ExerciseGymAliasesPanel />
                                        </div>
                                    </div>
                                </div>
                    </TabsContent>
                )}
                {/* Tab Esercizi senza immagine statica */}
                {userIsAdmin && (
                            <TabsContent value="noStaticImage" className="focus:outline-none flex-1 min-h-0 overflow-hidden">
                                <div className="h-full flex flex-col">
                                    <div className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-200 dark:border-gray-700 h-full flex flex-col">
                                        <div className="flex-1 min-h-0 overflow-y-auto p-6" style={{ overscrollBehavior: 'contain' }}>
                        <ExerciseNoStaticImagePanel onRefresh={handleRefresh} />
                                        </div>
                                    </div>
                                </div>
                    </TabsContent>
                )}
            </Tabs>
            </div>

            {/* Modal per la modifica dell'esercizio */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <ExerciseForm
                            exercise={editingExercise}
                            onExerciseAdded={handleExerciseSaved}
                            onClose={handleCloseModal}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExerciseAdminPanel;